export const EMPTY_SHIPMENT_VALUE = "---";

export const getShipmentCollection = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.$values)) return value.$values;
  return [];
};

export const getShipmentDisplayValue = (value) => {
  if (value === null || value === undefined || value === "") {
    return EMPTY_SHIPMENT_VALUE;
  }
  return value;
};

const hasRoutePointText = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "" && value !== EMPTY_SHIPMENT_VALUE;

export const getRoutePointDisplayName = (routePoint) => {
  if (hasRoutePointText(routePoint?.companyName)) return routePoint.companyName;
  if (hasRoutePointText(routePoint?.savedAddressName)) return routePoint.savedAddressName;

  const town = hasRoutePointText(routePoint?.town) ? routePoint.town : "";
  const city = hasRoutePointText(routePoint?.city) ? routePoint.city : "";
  return [town, city].filter(Boolean).join("/") || EMPTY_SHIPMENT_VALUE;
};

const withDisplayFallbacks = (source, keys) => {
  const result = { ...source };
  keys.forEach((key) => {
    result[key] = getShipmentDisplayValue(source?.[key]);
  });
  return result;
};

export const formatShipmentDate = (value, formatter) => {
  if (!value) return EMPTY_SHIPMENT_VALUE;
  const formattedValue = formatter(value);
  return formattedValue && !["Invalid date", "Geçersiz tarih"].includes(formattedValue)
    ? formattedValue
    : EMPTY_SHIPMENT_VALUE;
};

const withTextFallbacks = (source, keys) => {
  const result = { ...source };
  keys.forEach((key) => {
    result[key] = getShipmentDisplayValue(source?.[key]);
  });
  return result;
};

const normalizeRoutePoint = (routePoint) => {
  const source = routePoint ?? {};
  const normalized = withTextFallbacks(source, [
    "loadTime",
    "lateLoadTime",
    "location",
    "address",
    "companyName",
    "savedAddressName",
    "town",
    "city",
    "routePointPersonName",
    "routePointPersonPhoneNumber",
    "description",
  ]);

  return {
    ...normalized,
    displayName: getRoutePointDisplayName(source),
    loadMethods: {
      ...(source.loadMethods ?? {}),
      $values: getShipmentCollection(source.loadMethods).map((method) =>
        withTextFallbacks(method ?? {}, ["name"])
      ),
    },
  };
};

export const getShipmentRouteBounds = (routePoints) => {
  const values = getShipmentCollection(routePoints);
  const emptyRoutePoint = normalizeRoutePoint(null);

  return {
    firstRoutePoint: values[0] ?? emptyRoutePoint,
    lastRoutePoint: values[values.length - 1] ?? emptyRoutePoint,
  };
};

const normalizeWaitinLoad = (waitinLoad) => {
  const source = waitinLoad ?? {};
  const normalized = withDisplayFallbacks(withTextFallbacks(source, [
    "senderName",
    "senderAdressCity",
    "senderAdressTown",
    "receiverCity",
    "receiverAdressTown",
    "planningLoadDate",
    "planningUnloadDate",
    "paymentType",
    "loadUnit",
    "loadType",
    "loadDescription",
    "truckName",
    "trailerName",
    "truckType",
    "trailerType",
  ]), ["distance", "loadQuantity", "truckQuantity"]);

  return {
    ...normalized,
    aspiredLoads: {
      ...(source.aspiredLoads ?? {}),
      $values: getShipmentCollection(source.aspiredLoads),
    },
  };
};

export const normalizeShipmentListItem = (shipment) => {
  const source = shipment ?? {};
  const normalized = withDisplayFallbacks(withTextFallbacks(source, [
    "senderAdressCity",
    "senderAdressTown",
    "receiverCity",
    "receiverAdressTown",
    "planningLoadDate",
    "planningUnloadDate",
    "driverName",
    "driverPhoneNumber",
    "truckPlateNumber",
    "trailerPlateNumber",
    "companyName",
    "companyLoadNumber",
    "containerNumber",
    "sealNumber",
    "loadUnit",
    "loadType",
    "loadDescription",
    "truckName",
    "trailerName",
    "truckType",
    "trailerType",
  ]), [
    "distance",
    "freight",
    "loadOwnerFreight",
    "loadQuantity",
    "truckQuantity",
  ]);

  return {
    ...normalized,
    waitinLoad: normalizeWaitinLoad(source.waitinLoad),
    routePoints: {
      ...(source.routePoints ?? {}),
      $values: getShipmentCollection(source.routePoints).map(normalizeRoutePoint),
    },
    aspiredLoads: {
      ...(source.aspiredLoads ?? {}),
      $values: getShipmentCollection(source.aspiredLoads),
    },
    truckerUserRate: {
      ...(source.truckerUserRate ?? {}),
      $values: getShipmentCollection(source.truckerUserRate),
    },
  };
};

export const getShipmentMapRoute = (routePoints) => {
  return getShipmentCollection(routePoints)
    .map((routePoint) => ({
      lat: Number.parseFloat(String(routePoint?.lat ?? "").replace(",", ".")),
      lng: Number.parseFloat(String(routePoint?.lng ?? "").replace(",", ".")),
    }))
    .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng));
};
